import { useState, useRef } from 'react'
import { AtSign, Smile, Send } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useTranslation } from '@/i18n'
import EmojiPickerPopup from './EmojiPickerPopup'
import MentionAutocomplete from '@/components/Comments/MentionAutocomplete'

export type CommentInputSize = 'small' | 'regular' | 'expanded'

interface MentionState {
  isActive: boolean
  query: string
  startPos: number
  position: { top: number; left: number }
}

interface SharedCommentInputProps {
  value: string
  onChange: (value: string) => void
  onSubmit: () => void
  onMentionedUsersChange?: (userIds: string[]) => void
  placeholder?: string
  disabled?: boolean
  autoFocus?: boolean
  size?: CommentInputSize
  isInactive?: boolean
}

const SharedCommentInput = ({
  value,
  onChange,
  onSubmit,
  onMentionedUsersChange,
  placeholder,
  disabled = false,
  autoFocus = false,
  size = 'regular',
  isInactive = false,
}: SharedCommentInputProps) => {
  const { t } = useTranslation()
  const resolvedPlaceholder =
    placeholder ?? t('board:comments.add_placeholder', { defaultValue: 'Add a comment...' })
  const [showEmojiPicker, setShowEmojiPicker] = useState(false)
  const [isFocused, setIsFocused] = useState(false)
  const [mentionState, setMentionState] = useState<MentionState>({
    isActive: false,
    query: '',
    startPos: 0,
    position: { top: 0, left: 0 },
  })
  const [mentionedUsers, setMentionedUsers] = useState<Array<{ id: string; display: string }>>([])
  const inputRef = useRef<HTMLInputElement>(null)
  const emojiButtonRef = useRef<HTMLButtonElement>(null)

  const handleInputChange = (newValue: string) => {
    onChange(newValue)

    // Check for @ mention trigger - use queueMicrotask for immediate next tick without cleanup needs
    queueMicrotask(() => {
      const cursorPos = inputRef.current?.selectionStart || 0
      const textBeforeCursor = newValue.slice(0, cursorPos)
      const lastAtIndex = textBeforeCursor.lastIndexOf('@')

      if (lastAtIndex !== -1) {
        const textAfterAt = textBeforeCursor.slice(lastAtIndex + 1)

        // Check if there's no space after @ (active mention)
        if (!textAfterAt.includes(' ') && !textAfterAt.includes('\n')) {
          // Calculate position for autocomplete dropdown
          if (inputRef.current) {
            const input = inputRef.current
            const rect = input.getBoundingClientRect()

            setMentionState({
              isActive: true,
              query: textAfterAt,
              startPos: lastAtIndex,
              position: {
                top: rect.bottom + 5,
                left: rect.left,
              },
            })
          }
          return
        }
      }

      // Close mention autocomplete if no active mention
      if (mentionState.isActive) {
        setMentionState({ ...mentionState, isActive: false })
      }
    })
  }

  const handleMentionSelect = (user: {
    user_id: string
    name: string | null
    email: string | null
  }) => {
    const displayName = user.name || user.email || 'Unknown User'
    const beforeMention = value.slice(0, mentionState.startPos)
    const afterMention = value.slice(inputRef.current?.selectionStart || 0)
    const newValue = `${beforeMention}@${displayName} ${afterMention}`

    onChange(newValue)

    const newMentionedUsers = [...mentionedUsers, { id: user.user_id, display: displayName }]
    setMentionedUsers(newMentionedUsers)

    // Notify parent of mentioned users
    if (onMentionedUsersChange) {
      onMentionedUsersChange(newMentionedUsers.map(u => u.id))
    }

    setMentionState({ ...mentionState, isActive: false })

    // Focus back on input - use queueMicrotask for immediate next tick without cleanup needs
    queueMicrotask(() => {
      if (inputRef.current) {
        const newCursorPos = mentionState.startPos + displayName.length + 2
        inputRef.current.focus()
        inputRef.current.setSelectionRange(newCursorPos, newCursorPos)
      }
    })
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    // Don't handle Enter if mention autocomplete is active
    if (mentionState.isActive && ['ArrowUp', 'ArrowDown', 'Enter', 'Escape'].includes(e.key)) {
      return
    }

    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      onSubmit()
    }
  }

  const handleEmojiSelect = (emoji: string) => {
    // Insert emoji at current cursor position
    if (inputRef.current) {
      const start = inputRef.current.selectionStart || value.length
      const end = inputRef.current.selectionEnd || value.length
      const newContent = value.substring(0, start) + emoji + value.substring(end)
      onChange(newContent)

      // Reset cursor position after emoji - use queueMicrotask for immediate next tick without cleanup needs
      queueMicrotask(() => {
        if (inputRef.current) {
          inputRef.current.focus()
          inputRef.current.setSelectionRange(start + emoji.length, start + emoji.length)
        }
      })
    } else {
      onChange(value + emoji)
    }
    setShowEmojiPicker(false)
  }

  // Get size-specific styles
  const getSizeStyles = () => {
    switch (size) {
      case 'small':
        return {
          containerClass: 'gap-1',
          inputClass: 'px-2 py-1 text-sm',
          iconSize: 14,
          buttonSize: 'p-1',
          sendButtonClass: 'p-1.5',
        }
      case 'expanded':
        return {
          containerClass: 'gap-2',
          inputClass: 'px-3 py-2.5 text-sm',
          iconSize: 16,
          buttonSize: 'p-1',
          sendButtonClass: 'p-2',
        }
      default: // regular
        return {
          containerClass: 'gap-2',
          inputClass: 'px-2 py-2 text-sm',
          iconSize: 16,
          buttonSize: 'p-1',
          sendButtonClass: 'p-2',
        }
    }
  }

  const sizeStyles = getSizeStyles()
  const isActive = isFocused || value.length > 0

  return (
    <div className="relative">
      <div
        className={cn(
          'flex items-center gap-1 rounded-xl transition-all pr-1',
          isInactive ? 'bg-gray-50 dark:bg-dark-muted' : 'bg-white dark:bg-dark-card',
          isActive ? 'border-2 border-blue-300 shadow-[0_0_0_3px_rgba(37,99,235,0.1)]' : 'border border-gray-200 dark:border-dark-border',
        )}
      >
        <input
          ref={inputRef}
          type="text"
          value={value}
          onChange={(e) => handleInputChange(e.target.value)}
          onKeyDown={handleKeyDown}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          placeholder={resolvedPlaceholder}
          disabled={disabled || isInactive}
          autoFocus={autoFocus}
          className={cn(
            `flex-1 ${sizeStyles.inputClass} focus:outline-none bg-transparent border-none`,
            isInactive ? 'text-gray-400 dark:text-gray-500' : 'text-gray-900 dark:text-dark-text',
          )}
        />
        <button
          type="button"
          className={`${sizeStyles.buttonSize} hover:bg-gray-100 dark:hover:bg-dark-accent rounded-full transition-colors flex-shrink-0`}
          title={t('board:comments.mention_someone', { defaultValue: 'Mention someone' })}
          onPointerDown={(e) => {
            e.preventDefault()
            e.stopPropagation()
          }}
          onClick={(e) => {
            e.preventDefault()
            e.stopPropagation()
            onChange(value + '@')
            inputRef.current?.focus()
          }}
          disabled={disabled || isInactive}
        >
          <AtSign size={sizeStyles.iconSize} className={isInactive ? 'text-gray-300 dark:text-gray-600' : 'text-gray-500 dark:text-dark-text-muted'} />
        </button>
        <button
          type="button"
          ref={emojiButtonRef}
          className={`${sizeStyles.buttonSize} hover:bg-gray-100 dark:hover:bg-dark-accent rounded-full relative transition-colors flex-shrink-0`}
          title={t('board:comments.add_emoji', { defaultValue: 'Add emoji' })}
          onPointerDown={(e) => {
            e.preventDefault()
            e.stopPropagation()
          }}
          onClick={(e) => {
            e.preventDefault()
            e.stopPropagation()
            setShowEmojiPicker(!showEmojiPicker)
          }}
          disabled={disabled || isInactive}
        >
          <Smile size={sizeStyles.iconSize} className={isInactive ? 'text-gray-300 dark:text-gray-600' : 'text-gray-500 dark:text-dark-text-muted'} />
          {showEmojiPicker && (
            <EmojiPickerPopup
              onSelect={handleEmojiSelect}
              onClose={() => setShowEmojiPicker(false)}
              anchorRef={emojiButtonRef}
            />
          )}
        </button>
        <button
          type="button"
          onPointerDown={(e) => {
            e.preventDefault()
            e.stopPropagation()
          }}
          onClick={(e) => {
            e.preventDefault()
            e.stopPropagation()
            onSubmit()
          }}
          disabled={!value.trim() || disabled || isInactive}
          className={`${sizeStyles.buttonSize} hover:bg-blue-100 dark:hover:bg-blue-900/30 rounded-full transition-colors flex-shrink-0`}
          title={t('board:comments.send_message', { defaultValue: 'Send message' })}
        >
          <Send
            size={sizeStyles.iconSize}
            className={value.trim() && !isInactive ? 'text-blue-500' : 'text-gray-400 dark:text-gray-500'}
          />
        </button>
      </div>

      {mentionState.isActive && (
        <MentionAutocomplete
          query={mentionState.query}
          onSelect={handleMentionSelect}
          onClose={() => setMentionState({ ...mentionState, isActive: false })}
          position={mentionState.position}
        />
      )}
    </div>
  )
}

export default SharedCommentInput
